import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { config } from './../../config';
import {
  CreatePromptInputDto,
  CreateQuestionnaireDto,
  CreateQuestionnaireTemplateDto,
  CreateResponseDto,
  CreateSurveyInputDto,
  CreateSurveyResponseDto,
  PromptResponse,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireTemplate,
  QuestionnaireType,
} from './types';
import { PagedQueryResponse } from '../../../types';
import { ResultSetResponse } from '../resultSet/types';
import { slk } from 'survey-core';

export type TemplateType = {
  description: string;
  title: string;
  type: QuestionnaireType;
  isChecked?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class GptService {
  gptBaseUrl: any = config.baseApiv2Url;

  constructor(public http: HttpClient) {
    slk(config.surveyJsApiKey);
  }

  getGptTypes(resultSet?: ResultSetResponse): TemplateType[] {
    return [
      {
        description: 'Design your own questionnaire',
        title: 'AlgocyteGPt own-design-based questionnaire',
        type: QuestionnaireType.MANUAL,
      },
      {
        description: 'AlgocyteGPt <b>profile-based</b> questionnaire',
        title: 'AlgocyteGPt profile-based questionnaire',
        type: QuestionnaireType.PROFILE_BASED_AI,
      },
      {
        description:
          'Choose a previously designed questionnaire from the list below',
        title: 'AlgocyteGPt template-based questionnaire',
        type: QuestionnaireType.TEMPLATE_BASED,
      },
      {
        description: `AlgocyteGPt <b>test-based</b> questionnaire for the test ${
          resultSet ? `on ${resultSet.observationDate}` : ''
        }`,
        title: 'AlgocyteGPt test-based questionnaire',
        type: QuestionnaireType.RESULT_BASED_AI,
      },
    ];
  }

  getPrompt(data: CreatePromptInputDto): Observable<PromptResponse> {
    return this.http.post<PromptResponse>(
      `${this.gptBaseUrl}/questionnaire/prompt`,
      data
    );
  }

  getSuggestions(
    data: CreateSurveyInputDto
  ): Observable<CreateSurveyResponseDto> {
    return this.http.post<CreateSurveyResponseDto>(
      `${this.gptBaseUrl}/questionnaire/ai-suggestion`,
      data
    );
  }

  createResponse(data: CreateResponseDto): Observable<QuestionnaireResponse> {
    return this.http.post<QuestionnaireResponse>(
      `${this.gptBaseUrl}/questionnaire/response`,
      data
    );
  }

  deleteResponse(id: number): Observable<any> {
    return this.http.delete(`${this.gptBaseUrl}/questionnaire/response/${id}`);
  }

  getQuestionnaires({
    pageSize = 10,
    pageOffset = 0,
    type,
    sortField,
    sortOrder,
  }: {
    pageSize: number;
    pageOffset: number;
    type?: QuestionnaireType;
    sortField?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Observable<PagedQueryResponse<Questionnaire>> {
    return this.http.get<PagedQueryResponse<Questionnaire>>(
      `${
        this.gptBaseUrl
      }/questionnaires?pageSize=${pageSize}&pageOffset=${pageOffset}${
        type ? `&type=${type}` : ''
      }${
        sortField && sortOrder
          ? `&sortBy=${sortField}&sortOrder=${sortOrder}`
          : ''
      }`
    );
  }

  getQuestionnaireById(id: number): Observable<Questionnaire> {
    return this.http.get<Questionnaire>(
      `${this.gptBaseUrl}/questionnaires/${id}`
    );
  }

  createQuestionnaire(data: CreateQuestionnaireDto): Observable<Questionnaire> {
    return this.http.post<Questionnaire>(
      `${this.gptBaseUrl}/questionnaire`,
      data
    );
  }

  createTemplate(
    data: CreateQuestionnaireTemplateDto
  ): Observable<QuestionnaireTemplate> {
    return this.http.post<QuestionnaireTemplate>(
      `${this.gptBaseUrl}/questionnaire-template`,
      data
    );
  }

  updateTemplate(
    id: number,
    data: CreateQuestionnaireTemplateDto
  ): Observable<QuestionnaireTemplate> {
    return this.http.patch<QuestionnaireTemplate>(
      `${this.gptBaseUrl}/questionnaire-template/${id}`,
      data
    );
  }

  deleteTemplate(id: number): Observable<QuestionnaireTemplate> {
    return this.http.delete<QuestionnaireTemplate>(
      `${this.gptBaseUrl}/questionnaire-template/${id}`
    );
  }

  getTemplates({
    pageSize = 10,
    pageOffset = 0,
    sortOrder,
    sortField,
  }: {
    pageSize: number;
    pageOffset: number;
    sortField?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Observable<PagedQueryResponse<QuestionnaireTemplate>> {
    return this.http.get<PagedQueryResponse<QuestionnaireTemplate>>(
      `${
        this.gptBaseUrl
      }/questionnaire-template?pageSize=${pageSize}&pageOffset=${pageOffset}${
        sortField && sortOrder
          ? `&sortBy=${sortField}&sortOrder=${sortOrder}`
          : ''
      }`
    );
  }
}
